import React, { useEffect, useState } from 'react';
import productData from '../data/productData.json';
import ProductCard from '../components/ProductCard';
import SortControls from '../components/SortControls';

const Works: React.FC = () => {
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const productsPerPage = 8;

    // Sorting and filtering state
    const [sortBy, setSortBy] = useState<string>('dateUpdated');
    const [sortDirection, setSortDirection] = useState<string>('desc');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectedGames, setSelectedGames] = useState<string[]>([]);

    // All unique tags for the filter checkboxes
    const allTags = Array.from(new Set(productData.flatMap(product => product.tags)));
    const allGames = Array.from(new Set(productData.flatMap(product => product.game)));

    // Handler to update sorting criteria
    const handleSortChange = (sortField: string, sortDirection: string) => {
        setSortBy(sortField);
        setSortDirection(sortDirection);
        setCurrentPage(1);
    };

    // Handle tag filtering
    const handleTagChange = (tag: string) => {
        if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter(t => t !== tag));
        } else {
            setSelectedTags([...selectedTags, tag]);
        }
        setCurrentPage(1);
    };

    // Handle game filtering
    const handleGameCheckboxChange = (game: string) => {
        if (selectedGames.includes(game)) {
            setSelectedGames(selectedGames.filter((g) => g !== game));
        } else {
            setSelectedGames([...selectedGames, game]);
        }
        setCurrentPage(1);
    };

    // Sorting and filtering logic
    const filteredProducts = productData
        .filter(product => selectedTags.length === 0 || product.tags.some(tag => selectedTags.includes(tag))) // Tag filter
        .filter(product => selectedGames.length === 0 || selectedGames.includes(product.game)) // Game filter
        .sort((a, b) => {
            let valueA: number | string = 0;
            let valueB: number | string = 0;

            if (sortBy === 'datePosted') {
                // Sort by datePosted first
                const dateA = a.datePosted;
                const dateB = b.datePosted;
                valueA = new Date(dateA).getTime();
                valueB = new Date(dateB).getTime();
            }

            if (sortBy === 'dateUpdated') {
                // Sort by dateUpdated first (fallback to datePosted if dateUpdated is missing)
                const dateA = a.dateUpdated ? a.dateUpdated : a.datePosted;
                const dateB = b.dateUpdated ? b.dateUpdated : b.datePosted;
                valueA = new Date(dateA).getTime();
                valueB = new Date(dateB).getTime();
            }

            // First sort by the selected field (datePosted or dateUpdated)
            if (valueA !== valueB) {
                return sortDirection === 'asc' ? valueA > valueB ? 1 : -1 : valueA < valueB ? 1 : -1;
            }

            // If the dates are equal, then sort by id as the secondary criterion
            return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
        });


    // Pagination logic
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    // Pagination control
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handlePageClick = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    useEffect(() => {
        document.title = "SecretAria - Works";
    }, []);

    return (
        <div>
            <h1 className="text-center kanit-extrabold">Works</h1>
            <h5 className="text-center kanit-regular">{`${filteredProducts.length} ${filteredProducts.length === 1 ? 'result' : 'results'}`}</h5>
            <div>
                <div className="row mt-5 px-2 noto-sans-regular">
                    <div className="col-md-3 col-12 list-group mb-4">
                        {/* Sorting controls */}
                        <SortControls
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            onSortChange={handleSortChange}
                        />

                        {/* Game filters */}
                        <div className="filter-controls list-group-item">
                            <h4 className="kanit-regular">Game</h4>
                            {allGames.map((game) => (
                                <div key={game} className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={game}
                                        value={game}
                                        checked={selectedGames.includes(game)}
                                        onChange={() => handleGameCheckboxChange(game)}
                                    />
                                    <label className="form-check-label" htmlFor={game}>{game}</label>
                                </div>
                            ))}
                        </div>

                        {/* Tag filters */}
                        <div className="filter-controls list-group-item">
                            <h4 className="kanit-regular">Tags</h4>
                            {allTags.map(tag => (
                                <div key={tag} className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={tag}
                                        checked={selectedTags.includes(tag)}
                                        onChange={() => handleTagChange(tag)}
                                    />
                                    <label className="form-check-label" htmlFor={tag}>{tag}</label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="col-md-9 col-12">
                        {/* Product cards */}
                        <div className="row">
                            {currentProducts.map((product) => (
                                <ProductCard
                                    id={product.id}
                                    title={product.title}
                                    slug={product.slug}
                                    description={product.description}
                                    thumbnailUrl={product.thumbnailUrl}
                                    version={product.version}
                                    datePosted={product.datePosted}
                                    dateUpdated={product.dateUpdated}
                                    game={product.game}
                                    tags={product.tags}
                                />
                            ))}
                        </div>

                        {/* Pagination control */}
                        <nav aria-label="Page navigation example" className="mt-4">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <a className="page-link" onClick={handlePrevPage} aria-label="Previous">
                                        <span aria-hidden="true">&laquo;</span>
                                    </a>
                                </li>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                        <a className="page-link" onClick={() => handlePageClick(i + 1)}>{i + 1}</a>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <a className="page-link" onClick={handleNextPage} aria-label="Next">
                                        <span aria-hidden="true">&raquo;</span>
                                    </a>
                                </li>
                            </ul>
                        </nav>
                        
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Works;
